import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useBookingQueryStore } from '@/stores/bookingQuery.store';

export const useAgentFlowStore = defineStore('agentFlow', () => {
  const offerSelected = ref(false);
  const completedParameterTypes = ref([]);
  const checkInDate = ref(null);
  const checkOutDate = ref(null);
  const promotionalCode = ref(null);
  const occupancy = ref(null);
  const offer = ref(null);
  const rate = ref(null);
  const room = ref(null);
  const searchCurrencyUsed = ref(null);
  const selectedMealPlan = ref(null);
  const selectedAddons = ref([]);
  const selectedAddonsTotal = ref(0);
  const specialRequest = ref(null);
  const specialRequestDraft = ref(null);
  const specialRequestRequired = ref(false);
  const specialRequestConsentAccepted = ref(false);
  const specialRequestConsentPreview = ref(false);
  const specialRequestInputLocked = ref(false);
  const specialRequestPendingSubmission = ref(false);
  const hideAgentButton = ref(false);
  const requestGuestDetailsPaymentSummary = ref(null);
  const entryRoomRateId = ref(null);
  const entryDetailsView = ref(null);
  const entryContextSeeded = ref(false);
  const guestDetailsSubmitted = ref(false);

  const setHideAgentButton = (value) => {
    hideAgentButton.value = value;
  };

  const setRequestGuestDetailsPaymentSummary = (value) => {
    requestGuestDetailsPaymentSummary.value = value ?? null;
  };

  const hasAnySelectionDone = computed(() => {
    return (
      checkInDate.value || checkOutDate.value || occupancy.value || offer.value
    );
  });

  const setSelectedMealPlan = (value) => {
    selectedMealPlan.value = value;
  };

  const setSelectedAddons = (value) => {
    selectedAddons.value = value;
  };

  const setSelectedAddonsTotal = (value) => {
    selectedAddonsTotal.value = value;
  };

  const setSpecialRequest = (value) => {
    specialRequest.value = value;
  };

  const setSpecialRequestDraft = (value) => {
    specialRequestDraft.value = value;
  };

  const setSpecialRequestRequired = (value) => {
    specialRequestRequired.value = !!value;
  };

  const setSpecialRequestConsentAccepted = (value) => {
    specialRequestConsentAccepted.value = !!value;
  };

  const setSpecialRequestConsentPreview = (value) => {
    specialRequestConsentPreview.value = !!value;
  };

  const setSpecialRequestInputLocked = (value) => {
    specialRequestInputLocked.value = !!value;
  };

  const setSpecialRequestPendingSubmission = (value) => {
    specialRequestPendingSubmission.value = !!value;
  };

  const setCheckInDate = (value) => {
    checkInDate.value = value;
  };

  const setCheckOutDate = (value) => {
    checkOutDate.value = value;
  };

  const setPromotionalCode = (value) => {
    promotionalCode.value = value;
  };

  const setOccupancy = (value) => {
    occupancy.value = value;
  };

  const setOffer = (value) => {
    offer.value = value;
  };

  const setRate = (value) => {
    rate.value = value;
  };

  const setRoom = (value) => {
    room.value = value;
  };

  const setSearchCurrencyUsed = (value) => {
    searchCurrencyUsed.value = value ?? null;
  };

  const setEntryRoomRateContext = (roomRateId, detailsView = null) => {
    entryRoomRateId.value = roomRateId ?? null;
    entryDetailsView.value = detailsView ?? null;
    entryContextSeeded.value = false;
  };

  const clearEntryRoomRateContext = () => {
    entryRoomRateId.value = null;
    entryDetailsView.value = null;
    entryContextSeeded.value = false;
  };

  const setEntryContextSeeded = (value) => {
    entryContextSeeded.value = !!value;
  };

  const setGuestDetailsSubmitted = (value) => {
    guestDetailsSubmitted.value = !!value;
  };

  const resetOfferSelected = () => {
    offer.value = null;
    rate.value = null;
    room.value = null;
    requestGuestDetailsPaymentSummary.value = null;
  };

  const markParameterTypesCompleted = (types) => {
    const next = [...completedParameterTypes.value];
    types.forEach((t) => {
      if (!next.includes(t)) next.push(t);
    });
    completedParameterTypes.value = next;
  };

  const resetCompletedParameterTypes = () => {
    completedParameterTypes.value = [];
  };

  const clearCompletedParameterTypes = (types = []) => {
    const toClear = new Set(types);
    completedParameterTypes.value = completedParameterTypes.value.filter(
      (t) => !toClear.has(t),
    );
  };

  const syncFromBookingQuery = () => {
    const bookingQueryStore = useBookingQueryStore();
    const q = bookingQueryStore.query;

    if (q?.startDate) {
      const checkin = new Date(q.startDate);
      if (!Number.isNaN(checkin.getTime())) {
        checkin.setHours(0, 0, 0, 0);
        setCheckInDate(checkin);
      }
    } else {
      setCheckInDate(null);
    }

    if (q?.endDate) {
      const checkout = new Date(q.endDate);
      if (!Number.isNaN(checkout.getTime())) {
        checkout.setHours(0, 0, 0, 0);
        setCheckOutDate(checkout);
      }
    } else {
      setCheckOutDate(null);
    }

    if (Array.isArray(q?.occupancy) && q.occupancy.length > 0) {
      setOccupancy(
        q.occupancy.map((room) => ({
          nrAdults: room.nrAdults,
          nrChildren: room.nrChildren,
          nrBabies: room.nrBabies,
          children_ages: [...(room.children_ages || [])],
        })),
      );
    } else {
      setOccupancy(null);
    }

    const promos = bookingQueryStore.activatedPromotionalCodes;
    if (promos?.length > 0) {
      setPromotionalCode(promos[0].value);
    } else if (q?.campaign_code) {
      setPromotionalCode(q.campaign_code);
    } else {
      setPromotionalCode(null);
    }
  };
  /** Clear checkout terms/special-request gating (e.g. when leaving the agent route). */
  const resetSpecialRequestState = () => {
    specialRequest.value = null;
    specialRequestDraft.value = null;
    specialRequestRequired.value = false;
    specialRequestConsentAccepted.value = false;
    specialRequestConsentPreview.value = false;
    specialRequestInputLocked.value = false;
    specialRequestPendingSubmission.value = false;
    requestGuestDetailsPaymentSummary.value = null;
  };

  /** Reset all agent flow state (e.g. when user re-enters the agent / thread is reset). */
  const reset = () => {
    offerSelected.value = false;
    completedParameterTypes.value = [];
    checkInDate.value = null;
    checkOutDate.value = null;
    promotionalCode.value = null;
    occupancy.value = null;
    offer.value = null;
    rate.value = null;
    room.value = null;
    selectedMealPlan.value = null;
    selectedAddons.value = [];
    selectedAddonsTotal.value = 0;
    guestDetailsSubmitted.value = false;
    clearEntryRoomRateContext();
    resetSpecialRequestState();
  };

  return {
    offerSelected,
    checkInDate,
    checkOutDate,
    promotionalCode,
    occupancy,
    offer,
    rate,
    room,
    searchCurrencyUsed,
    completedParameterTypes,
    setCheckInDate,
    setCheckOutDate,
    setPromotionalCode,
    setOccupancy,
    syncFromBookingQuery,
    setOffer,
    setRate,
    setRoom,
    setSearchCurrencyUsed,
    resetOfferSelected,
    markParameterTypesCompleted,
    resetCompletedParameterTypes,
    clearCompletedParameterTypes,
    reset,
    resetSpecialRequestState,
    hasAnySelectionDone,
    setSelectedMealPlan,
    setSelectedAddons,
    setSelectedAddonsTotal,
    selectedMealPlan,
    selectedAddons,
    selectedAddonsTotal,
    specialRequest,
    setSpecialRequest,
    specialRequestDraft,
    setSpecialRequestDraft,
    specialRequestRequired,
    setSpecialRequestRequired,
    specialRequestConsentAccepted,
    setSpecialRequestConsentAccepted,
    specialRequestConsentPreview,
    setSpecialRequestConsentPreview,
    specialRequestInputLocked,
    setSpecialRequestInputLocked,
    specialRequestPendingSubmission,
    setSpecialRequestPendingSubmission,
    setHideAgentButton,
    hideAgentButton,
    requestGuestDetailsPaymentSummary,
    setRequestGuestDetailsPaymentSummary,
    entryRoomRateId,
    entryDetailsView,
    entryContextSeeded,
    setEntryRoomRateContext,
    clearEntryRoomRateContext,
    setEntryContextSeeded,
    guestDetailsSubmitted,
    setGuestDetailsSubmitted,
  };
});
